import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';

const primaryColor = '#5757b0';

/**
 * Shared JSON table rendering logic
 * Used by TagTooltip, TimeEventJsonViewer, and other components that need to display JSON as tables
 */
const JsonTableRenderer = ({ 
  data, 
  maxHeight = 200, 
  maxItems = Infinity,
  showIndex = true,
  compact = true 
}) => {
  const effectiveMaxItems = Number.isFinite(maxItems) ? maxItems : Infinity;
  const hasBoundedHeight = maxHeight !== null && maxHeight !== undefined && maxHeight !== 'none';

  // Ensure data is parsed if it's a string
  let parsedData = data;
  if (typeof data === 'string') {
    try {
      parsedData = JSON.parse(data);
    } catch (e) {
      // If parsing fails, treat as regular text
      return (
        <Box sx={{
          backgroundColor: '#fff',
          padding: '12px 16px',
          borderRadius: '8px',
          ...(hasBoundedHeight ? { maxHeight } : {}),
          overflow: hasBoundedHeight ? 'auto' : 'visible',
          border: `1px solid ${primaryColor}20`
        }}>
          <Typography variant="caption" sx={{ 
            wordBreak: 'break-word',
            color: '#333',
            fontSize: compact ? '0.75rem' : '0.85rem',
            lineHeight: 1.5
          }}>
            {String(data)}
          </Typography>
        </Box>
      );
    }
  }

  // Check if it's JSON-like data
  if (!parsedData || (typeof parsedData !== 'object' && !Array.isArray(parsedData))) {
    return (
      <Box sx={{
        backgroundColor: '#fff',
        padding: '12px 16px',
        borderRadius: '8px',
        ...(hasBoundedHeight ? { maxHeight } : {}),
        overflow: hasBoundedHeight ? 'auto' : 'visible',
        border: `1px solid ${primaryColor}20`
      }}>
        <Typography variant="caption" sx={{ 
          wordBreak: 'break-word',
          color: '#333',
          fontSize: compact ? '0.75rem' : '0.85rem',
          lineHeight: 1.5
        }}>
          {String(data)}
        </Typography>
      </Box>
    );
  }

  // Render arrays
  if (Array.isArray(parsedData)) {
    // Check if array contains objects and get all unique keys
    const hasObjects = parsedData.some(item => typeof item === 'object' && item !== null);
    
    if (hasObjects) {
      // Get all unique keys from all objects in the array
      const allKeys = [...new Set(
        parsedData
          .filter(item => typeof item === 'object' && item !== null)
          .flatMap(item => Object.keys(item))
      )];
      
      return (
        <TableContainer component={Paper} sx={{ 
          mt: 2, 
          ...(hasBoundedHeight ? { maxHeight } : {}),
          width: '100%',
          overflow: hasBoundedHeight ? 'auto' : 'visible',
          boxShadow: 'none',
          border: `1px solid ${primaryColor}20` 
        }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {showIndex && (
                  <TableCell sx={{ 
                    fontWeight: 'bold', 
                    fontSize: compact ? '0.7rem' : '0.8rem',
                    backgroundColor: `${primaryColor}10`,
                    color: primaryColor,
                    width: '30px'
                  }}>
                    #
                  </TableCell>
                )}
                {allKeys.map(key => (
                  <TableCell key={key} sx={{ 
                    fontWeight: 'bold', 
                    fontSize: compact ? '0.7rem' : '0.8rem',
                    backgroundColor: `${primaryColor}10`,
                    color: primaryColor,
                    maxWidth: compact ? '120px' : undefined,
                    overflow: compact ? 'hidden' : 'visible',
                    textOverflow: compact ? 'ellipsis' : 'clip',
                    whiteSpace: compact ? 'nowrap' : 'normal',
                    wordBreak: compact ? 'normal' : 'break-word',
                    minWidth: compact ? 'auto' : '100px'
                  }}>
                    {key}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {parsedData.slice(0, effectiveMaxItems).map((item, index) => (
                <TableRow key={index}>
                  {showIndex && (
                    <TableCell sx={{ fontSize: compact ? '0.7rem' : '0.8rem', color: '#666' }}>
                      {index}
                    </TableCell>
                  )}
                  {allKeys.map(key => (
                    <TableCell key={key} sx={{ 
                      fontSize: compact ? '0.7rem' : '0.8rem', 
                      maxWidth: compact ? '150px' : undefined,
                      overflow: compact ? 'hidden' : 'visible',
                      textOverflow: compact ? 'ellipsis' : 'clip',
                      whiteSpace: compact ? 'nowrap' : 'normal',
                      wordBreak: compact ? 'normal' : 'break-word',
                      verticalAlign: 'top'
                    }}>
                      {typeof item === 'object' && item !== null ? (
                        <Typography variant="caption" sx={{ 
                          fontSize: compact ? '0.7rem' : '0.8rem',
                          overflow: compact ? 'hidden' : 'visible',
                          textOverflow: compact ? 'ellipsis' : 'clip',
                          whiteSpace: compact ? 'nowrap' : 'normal',
                          wordBreak: compact ? 'normal' : 'break-word',
                          display: 'block'
                        }}>
                          {item[key] !== undefined ? String(item[key]) : '-'}
                        </Typography>
                      ) : (
                        <Typography variant="caption" sx={{ 
                          fontSize: compact ? '0.7rem' : '0.8rem',
                          overflow: compact ? 'hidden' : 'visible',
                          textOverflow: compact ? 'ellipsis' : 'clip',
                          whiteSpace: compact ? 'nowrap' : 'normal',
                          wordBreak: compact ? 'normal' : 'break-word',
                          display: 'block'
                        }}>
                          {String(item)}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {parsedData.length > effectiveMaxItems && (
                <TableRow>
                  <TableCell colSpan={allKeys.length + (showIndex ? 1 : 0)} sx={{ 
                    textAlign: 'center', 
                    fontStyle: 'italic', 
                    color: '#999',
                    fontSize: compact ? '0.65rem' : '0.75rem'
                  }}>
                    ... and {parsedData.length - effectiveMaxItems} more items
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      );
    } else {
      // Original simple array rendering for non-object arrays
      return (
        <TableContainer component={Paper} sx={{ 
          mt: 2, 
          ...(hasBoundedHeight ? { maxHeight } : {}),
          width: '100%',
          overflow: hasBoundedHeight ? 'auto' : 'visible',
          boxShadow: 'none',
          border: `1px solid ${primaryColor}20` 
        }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {showIndex && (
                  <TableCell sx={{ 
                    fontWeight: 'bold', 
                    fontSize: compact ? '0.7rem' : '0.8rem',
                    backgroundColor: `${primaryColor}10`,
                    color: primaryColor
                  }}>
                    #
                  </TableCell>
                )}
                <TableCell sx={{ 
                  fontWeight: 'bold', 
                  fontSize: compact ? '0.7rem' : '0.8rem',
                  backgroundColor: `${primaryColor}10`,
                  color: primaryColor
                }}>
                  Value
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parsedData.slice(0, effectiveMaxItems).map((item, index) => (
                <TableRow key={index}>
                  {showIndex && (
                    <TableCell sx={{ fontSize: compact ? '0.7rem' : '0.8rem', color: '#666' }}>
                      {index}
                    </TableCell>
                  )}
                  <TableCell sx={{ fontSize: compact ? '0.7rem' : '0.8rem' }}>
                    {String(item)}
                  </TableCell>
                </TableRow>
              ))}
              {parsedData.length > effectiveMaxItems && (
                <TableRow>
                  <TableCell colSpan={showIndex ? 2 : 1} sx={{ 
                    textAlign: 'center', 
                    fontStyle: 'italic', 
                    color: '#999',
                    fontSize: compact ? '0.65rem' : '0.75rem'
                  }}>
                    ... and {parsedData.length - effectiveMaxItems} more items
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }
  } else {
    // Render objects
    return (
      <TableContainer component={Paper} sx={{ 
        mt: 2, 
        ...(hasBoundedHeight ? { maxHeight } : {}),
        width: '100%',
        overflow: hasBoundedHeight ? 'auto' : 'visible',
        boxShadow: 'none',
        border: `1px solid ${primaryColor}20` 
      }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ 
                fontWeight: 'bold', 
                fontSize: compact ? '0.7rem' : '0.8rem',
                backgroundColor: `${primaryColor}10`,
                color: primaryColor
              }}>
                Key
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 'bold', 
                fontSize: compact ? '0.7rem' : '0.8rem',
                backgroundColor: `${primaryColor}10`,
                color: primaryColor
              }}>
                Value
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(parsedData || {}).slice(0, effectiveMaxItems).map(([key, value]) => (
              <TableRow key={key}>
                <TableCell sx={{ fontSize: compact ? '0.7rem' : '0.8rem', fontWeight: '500', color: primaryColor }}>
                  {key}
                </TableCell>
                <TableCell sx={{ fontSize: compact ? '0.7rem' : '0.8rem' }}>
                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                </TableCell>
              </TableRow>
            ))}
            {Object.keys(parsedData || {}).length > effectiveMaxItems && (
              <TableRow>
                <TableCell colSpan={2} sx={{ 
                  textAlign: 'center', 
                  fontStyle: 'italic', 
                  color: '#999',
                  fontSize: compact ? '0.65rem' : '0.75rem'
                }}>
                  ... and {Object.keys(parsedData || {}).length - effectiveMaxItems} more fields
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }
};

export default JsonTableRenderer;
